import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SettingsPanel from './SettingsPanel';
import ToggleSwitch from './ToggleSwitch';
import rekordboxLogo from '../assets/rekordbox-logo.png';
import seratoLogo from '../assets/serato-logo-10px.png';
import traktorLogo from '../assets/traktor-logo.png';
import virtualdjLogo from '../assets/virtual-dj-logo.png';
import {
    setDetectCues,
    setSensitivity,
    setMaxHotCues,
    setCueMode,
    setMinDistanceBars,
    setQuantizeCuesToGrid,
    setDownbeatMode,
    setDownbeatCompare,
    setKeepOriginalsUntouched,
    setWriteFileTags,
    setDetectLoops,
    setLoopBars,
    setMaxLoops,
    setLoopSensitivity,
    setLoopFocus,
    setSnapToBeat,
    setLoopMode,
    setAutoActiveLoop,
    setTargetRekordbox,
    setTargetSerato,
    setTargetTraktor,
    setTargetVirtualdj,
} from '../redux/slices/cuePointsSettingsSlice';

const LOOP_BAR_CHOICES = [4, 8, 16, 32];

const swLabel = (logo, name) => (
    <span className="cue-sw-label"><img src={logo} alt="" className="cue-sw-logo" />{name}</span>
);

const CuePointsSettings = () => {
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.cuePointsSettings);
    const {
        detectCues = true, sensitivity, maxHotCues, minDistanceBars, cueMode = 'hot',
        quantizeCuesToGrid = true,
        detectLoops = true, loopBars = [4, 8, 16, 32], maxLoops = 4, loopSensitivity = 0.5,
        loopFocus = 'balanced', snapToBeat = true,
        loopMode = 'spare', autoActiveLoop = false,
    } = settings;
    const isMemoryMode = cueMode === 'memory';

    // Toggle a bar-length chip; never let the set become empty.
    const toggleLoopBar = (bars) => {
        const set = new Set(loopBars);
        if (set.has(bars)) { if (set.size > 1) set.delete(bars); }
        else set.add(bars);
        dispatch(setLoopBars(LOOP_BAR_CHOICES.filter((b) => set.has(b))));
    };

    const loopSensWord = loopSensitivity >= 0.7 ? 'Strict' : loopSensitivity >= 0.4 ? 'Balanced' : 'Generous';
    // Guard against any bad/missing persisted value so the controls never break.
    const safeMaxLoops = (Number.isFinite(maxLoops) && maxLoops >= 1 && maxLoops <= 8) ? maxLoops : 6;
    const safeMaxHot = (Number.isFinite(maxHotCues) && maxHotCues >= 1 && maxHotCues <= 8) ? maxHotCues : 8;

    // Persist settings immediately on every change (so values survive even an
    // abrupt quit), plus on app close.
    useEffect(() => {
        try { localStorage.setItem('cuePointsSettings_v11', JSON.stringify(settings)); } catch (e) { /* noop */ }
    }, [settings]);
    const settingsRef = useRef(settings);
    settingsRef.current = settings;
    useEffect(() => {
        const saveOnClose = () => {
            try {
                localStorage.setItem('cuePointsSettings_v11', JSON.stringify(settingsRef.current));
            } catch (e) { /* noop */ }
        };
        window.addEventListener('beforeunload', saveOnClose);
        return () => window.removeEventListener('beforeunload', saveOnClose);
    }, []);

    const sensitivityWord = sensitivity >= 0.9 ? 'Strict' : sensitivity >= 0.78 ? 'Balanced' : 'Generous';

    const detectionControls = (
        <>
            <h4 className="title">Cues</h4>
            <div className="cue-setting cue-setting-toggle">
                <ToggleSwitch
                    id="detectCues"
                    checked={detectCues}
                    onChange={(v) => dispatch(setDetectCues(v))}
                    label="Detect cues"
                />
                <p className="cue-setting-help">Auto-detect cue points with the AI model. Off = loops only.</p>
            </div>

            <div className={`cue-section-body ${detectCues ? '' : 'is-disabled'}`}>
            <div className="cue-setting">
                <label className="cue-setting-label">
                    Sensitivity
                    <span className="cue-setting-value">{Number(sensitivity).toFixed(2)} · {sensitivityWord}</span>
                </label>
                <input
                    type="range" min="0.5" max="0.99" step="0.01" value={sensitivity}
                    onChange={(e) => dispatch(setSensitivity(parseFloat(e.target.value)))}
                    className="cue-range"
                />
                <p className="cue-setting-help">
                    How selective detection is. <strong>Higher</strong> = fewer cues, only the
                    most confident moments. <strong>Lower</strong> = more cues, including
                    subtler intros &amp; breakdowns.
                </p>
            </div>

            <div className="cue-setting">
                <label className="cue-setting-label">Save cues as</label>
                <div className="cue-segmented">
                    {[['hot', 'Hot cues'], ['memory', 'Memory cues']].map(([val, lbl]) => (
                        <button
                            key={val}
                            type="button"
                            className={`cue-seg-btn ${cueMode === val ? 'active' : ''}`}
                            onClick={() => dispatch(setCueMode(val))}
                        >
                            {lbl}
                        </button>
                    ))}
                </div>
                <p className="cue-setting-help">
                    <strong>Hot cues</strong> = trigger pads you tap live. <strong>Memory cues</strong> = a
                    roadmap you scroll to (pads left empty). Memory cues are Rekordbox-only — Serato,
                    Traktor &amp; VirtualDJ always get hot cues.
                </p>
            </div>

            <div className="cue-setting" style={isMemoryMode ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
                <label className="cue-setting-label">Max hot cues</label>
                <div className="cue-num-field">
                    <input
                        type="text" inputMode="numeric" value={safeMaxHot} disabled={isMemoryMode}
                        onChange={(e) => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n)) dispatch(setMaxHotCues(Math.max(1, Math.min(8, n)))); }}
                    />
                    <div className="cue-num-arrows">
                        <button type="button" tabIndex={-1} onClick={() => dispatch(setMaxHotCues(Math.min(8, safeMaxHot + 1)))} className="num-up" aria-label="increase" />
                        <button type="button" tabIndex={-1} onClick={() => dispatch(setMaxHotCues(Math.max(1, safeMaxHot - 1)))} className="num-down" aria-label="decrease" />
                    </div>
                </div>
                <p className="cue-setting-help">The strongest cues become colored hot-cue pads (max 8); any extra are saved as memory cues.</p>
            </div>

            <div className="cue-setting">
                <label className="cue-setting-label">Min distance</label>
                <div className="cue-num-field">
                    <input
                        type="text" inputMode="numeric" value={minDistanceBars}
                        onChange={(e) => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n)) dispatch(setMinDistanceBars(Math.max(1, Math.min(64, n)))); }}
                    />
                    <div className="cue-num-arrows">
                        <button type="button" tabIndex={-1} onClick={() => dispatch(setMinDistanceBars(Math.min(64, (minDistanceBars || 16) + 1)))} className="num-up" aria-label="increase" />
                        <button type="button" tabIndex={-1} onClick={() => dispatch(setMinDistanceBars(Math.max(1, (minDistanceBars || 16) - 1)))} className="num-down" aria-label="decrease" />
                    </div>
                </div>
                <p className="cue-setting-help">Minimum spacing between cues, so they aren't bunched together.</p>
            </div>

            <div className="cue-setting cue-setting-toggle">
                <ToggleSwitch
                    id="quantizeCuesToGrid"
                    checked={quantizeCuesToGrid}
                    onChange={(v) => dispatch(setQuantizeCuesToGrid(v))}
                    label="Snap cues to beatgrid"
                />
                <p className="cue-setting-help">
                    Nudge detected cues onto the nearest beat (loops anchored to them
                    follow). Never moves a cue more than half a beat.
                </p>
            </div>
            {process.env.NODE_ENV !== 'production' && (
                <div className="cue-setting-row cue-setting-dev" title="Dev-only. The merged downbeat vote (beat-tracker model + first kick/backbeat + cue votes) is the default; Compare also runs the previous kick-flux vote from the same analysis and adds a Grid switch above the editor.">
                    <ToggleSwitch
                        id="downbeatCompare"
                        checked={settings.downbeatCompare !== false}
                        onChange={(v) => dispatch(setDownbeatCompare(v))}
                        label="Compare downbeat votes (dev)"
                    />
                    <ToggleSwitch
                        id="downbeatModeKick"
                        checked={settings.downbeatMode === 'kick'}
                        onChange={(v) => dispatch(setDownbeatMode(v ? 'kick' : 'merged'))}
                        label="Use the previous (kick-flux) downbeat vote as primary"
                    />
                </div>
            )}

            </div>

            <h4 className="title loops-divider">Loops</h4>

            <div className="cue-setting cue-setting-toggle">
                <ToggleSwitch
                    id="detectLoops"
                    checked={detectLoops}
                    onChange={(v) => dispatch(setDetectLoops(v))}
                    label="Detect loops"
                />
                <p className="cue-setting-help">Generate beat-aligned loops alongside cues (in the same pass). Off = cues only.</p>
            </div>

            <div className={`cue-section-body ${detectLoops ? '' : 'is-disabled'}`}>
            <div className="cue-setting">
                <label className="cue-setting-label">Loop lengths</label>
                <div className="cue-chip-row">
                    {LOOP_BAR_CHOICES.map((bars) => (
                        <button
                            key={bars}
                            type="button"
                            className={`cue-chip ${loopBars.includes(bars) ? 'active' : ''}`}
                            onClick={() => toggleLoopBar(bars)}
                        >
                            {bars}
                        </button>
                    ))}
                </div>
                <p className="cue-setting-help">Which loop lengths to consider. The cleanest-tiling length is chosen per spot.</p>
            </div>

            <div className="cue-setting">
                <label className="cue-setting-label">Max loops</label>
                <div className="cue-num-field">
                    <input
                        type="text" inputMode="numeric" value={safeMaxLoops}
                        onChange={(e) => { const n = parseInt(e.target.value, 10); if (Number.isFinite(n)) dispatch(setMaxLoops(Math.max(1, Math.min(8, n)))); }}
                    />
                    <div className="cue-num-arrows">
                        <button type="button" tabIndex={-1} onClick={() => dispatch(setMaxLoops(Math.min(8, safeMaxLoops + 1)))} className="num-up" aria-label="increase" />
                        <button type="button" tabIndex={-1} onClick={() => dispatch(setMaxLoops(Math.max(1, safeMaxLoops - 1)))} className="num-down" aria-label="decrease" />
                    </div>
                </div>
                <p className="cue-setting-help">How many loops to keep per track (the highest-quality ones).</p>
            </div>

            <div className="cue-setting">
                <label className="cue-setting-label">
                    Loop sensitivity
                    <span className="cue-setting-value">{Number(loopSensitivity).toFixed(2)} · {loopSensWord}</span>
                </label>
                <input
                    type="range" min="0" max="1" step="0.05" value={loopSensitivity}
                    onChange={(e) => dispatch(setLoopSensitivity(parseFloat(e.target.value)))}
                    className="cue-range"
                />
                <p className="cue-setting-help">
                    <strong>Higher</strong> = fewer, only the cleanest seamless loops.
                    <strong> Lower</strong> = more loops, including looser sections.
                </p>
            </div>

            <div className="cue-setting">
                <label className="cue-setting-label">Loop focus</label>
                <div className="cue-segmented">
                    {[['grooves', 'Grooves'], ['balanced', 'Balanced'], ['drops', 'Drops']].map(([val, lbl]) => (
                        <button
                            key={val}
                            type="button"
                            className={`cue-seg-btn ${loopFocus === val ? 'active' : ''}`}
                            onClick={() => dispatch(setLoopFocus(val))}
                        >
                            {lbl}
                        </button>
                    ))}
                </div>
                <p className="cue-setting-help">What to prioritise when picking loop spots — steady <strong>grooves</strong>, the <strong>drops</strong>, or a mix.</p>
            </div>

            <div className="cue-setting cue-setting-toggle">
                <ToggleSwitch
                    id="snapToBeat"
                    checked={snapToBeat}
                    onChange={(v) => dispatch(setSnapToBeat(v))}
                    label="Snap loops to the beat grid"
                />
                <p className="cue-setting-help">Keeps loop edges on bar lines so they repeat seamlessly. Recommended on.</p>
            </div>

            <div className="cue-setting">
                <label className="cue-setting-label">Loops on Rekordbox pads</label>
                <div className="cue-segmented">
                    {[['spare', 'Spare pads'], ['memory', 'Memory only'], ['priority', 'Pads first']].map(([val, lbl]) => (
                        <button
                            key={val}
                            type="button"
                            className={`cue-seg-btn ${loopMode === val ? 'active' : ''}`}
                            onClick={() => dispatch(setLoopMode(val))}
                        >
                            {lbl}
                        </button>
                    ))}
                </div>
                <p className="cue-setting-help">
                    Rekordbox's 8 pads are one bank shared by cues and loops.
                    <strong> Spare pads</strong> = cues get pads first, loops take what's left.
                    <strong> Memory only</strong> = loops always become memory loops (call them
                    with CUE/LOOP CALL on a CDJ). <strong>Pads first</strong> = loops win the pads.
                    Extras are never lost — they become memory cues/loops.
                </p>
            </div>

            <div className="cue-setting cue-setting-toggle">
                <ToggleSwitch
                    id="autoActiveLoop"
                    checked={autoActiveLoop}
                    onChange={(v) => dispatch(setAutoActiveLoop(v))}
                    label="Auto Active Loop (last loop)"
                />
                <p className="cue-setting-help">
                    Arm the last loop as Rekordbox's <strong>Active Loop</strong> — CDJs engage it
                    automatically when playback reaches it, so the track never runs out under you.
                    One per track; picking a loop manually in the editor overrides this.
                </p>
            </div>
            </div>
        </>
    );

    const writeHelp = (
        <p className="cue-setting-help cue-section-help">
            Where to save the generated cues. Pick the DJ apps whose libraries you want updated.
        </p>
    );

    return (
        <SettingsPanel
            filtersTitle={null}
            filtersComponent={detectionControls}
            showFilterSeparator={true}
            settingsFirst={false}
            title="Write cues & loops to"
            settings={[
                { id: 'writeHelp', customComponent: writeHelp },
                { id: 'targetRekordbox', selector: 'targetRekordbox', label: swLabel(rekordboxLogo, 'Rekordbox'), action: setTargetRekordbox },
                { id: 'targetSerato', selector: 'targetSerato', label: swLabel(seratoLogo, 'Serato'), action: setTargetSerato },
                { id: 'targetTraktor', selector: 'targetTraktor', label: swLabel(traktorLogo, 'Traktor'), action: setTargetTraktor },
                { id: 'targetVirtualdj', selector: 'targetVirtualdj', label: swLabel(virtualdjLogo, 'VirtualDJ'), action: setTargetVirtualdj },
                { id: 'writeFileTags', selector: 'writeFileTags', label: 'Embed cue tags in the audio files', action: setWriteFileTags },
                { id: 'keepOriginalsUntouched', selector: 'keepOriginalsUntouched', label: 'Keep original files untouched (write to a [cues] copy)', action: setKeepOriginalsUntouched },
            ]}
            sliceSelector={(state) => state.cuePointsSettings}
        />
    );
};

export default CuePointsSettings;
